import {computed, effect, inject, Injectable, isDevMode, signal} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {PrimusBackendInstanceService, PrimusInstanceDetails} from '../core/primus-backend-instance.service';
import {BackendStatusResult} from '../core/definitions/backend-status-result';
import {TranslateService} from '@ngx-translate/core';
import {HelpdeskApiService, TipsArticle} from './helpdesk-api.service';
import {catchError, map, retry, switchMap, tap} from 'rxjs/operators';
import {of, throwError, timer} from 'rxjs';
import {fetchWithTimeout} from './fetch-with-timeout';

/**
 * If any CMS API call takes longer than this, perform a health check
 */
export const TIMEOUT_TO_CHECK_HEALTH_AFTER_SLOW_CMS_API_MS = 600;
/**
 * When performing a health check, the system is considered hibernating
 * if it takes longer than this time to respond.
 */
const TIMEOUT_WHEN_SYSTEM_IS_CONSIDERED_HIBERNATING_MS = 2000;
// /status can occasionally be slower than wake-trigger probes; keep a slightly
// longer timeout to avoid false "unknown" flips that cause splash flicker.
const TIMEOUT_STATUS_FETCH_MS = 5000;
// Timeout for the deep health endpoint (/healthcheck/check_health/all).
const TIMEOUT_HEALTHCHECK_FETCH_MS = 10_000;
// Delay between wake-up rechecks while waiting for backend dependencies.
const TIMEOUT_RETRY_HEALTHCHECK_AFTER_MS = 5000;
// Skip duplicate checks shortly after a successful health check.
const TIMEOUT_SKIP_RECENT_HEALTHCHECK_MS = 10_000;
// After this duration we show "taking longer than expected" guidance in the wake UI.
// This does not stop or fail wake recovery; it only affects user-facing messaging.
const TIMEOUT_WAKE_LONG_WAIT_MESSAGE_MS = 60_000;
// Short cooldown after timeout/error before auto-retrying wake.
const TIMEOUT_WAKEUP_ERROR_RETRY_BLOCK_MS = 15_000;
// Prevent overlay flicker for very short wake transitions.
const TIMEOUT_WAKE_UI_DEBOUNCE_MS = 350;
// Clamp server-provided retryAfter values.
const STATUS_RETRY_AFTER_MIN_MS = 1000;
const STATUS_RETRY_AFTER_MAX_MS = 15_000;

export interface HealthCheckResult {
  check_time_end?: string,
  check_time_start?: string,
  db?: {
    error?: string,
    ping?: string
  },
  memory?: number,
  resident?: number,
  solr?: {
    responseHeader?: {
      QTime?: number,
      params?: {
        df?: string,
        distrib?: string,
        echoParams?: string,
        preferLocalShards?: string,
        q?: string,
        rid?: string,
        rows?: string
      },
      status?: number,
      zkConnected?: boolean
    },
    status?: string
  },
  stack_size?: number
}

export type HibernationWakeState =
  | 'idle'
  | 'checking-status'
  | 'waiting-for-up'
  | 'checking-health'
  | 'failed';

type BackendStatusState = 'up' | 'down' | 'unknown' | 'unsupported';

interface BackendStatusSnapshot {
  state: BackendStatusState;
  reason: string | null;
  retryAfterMs: number;
}
/**
 * Single entrypoint for hibernation state and wake operations.
 * - Owns wake state machine + backend health checks.
 * - Owns wake-overlay tips state for the waiting UI.
 */
@Injectable({
  providedIn: 'root'
})
export class HibernationService {
  private readonly translation: TranslateService = inject(TranslateService);
  private readonly helpdeskApiService: HelpdeskApiService = inject(HelpdeskApiService);

  private _connectionInterrupted = signal(false);
  private _skipHealthCheck = signal(false);
  private _wakeState = signal<HibernationWakeState>('idle');
  private _isWakeFlowActive = computed(() => {
    const state = this._wakeState();
    return state === 'waiting-for-up';
  });
  get isWakeFlowActive() {
    return this._isWakeFlowActive;
  }
  private _isWakeFlowUiActive = signal(false);
  get isWakeFlowUiActive() {
    return this._isWakeFlowUiActive.asReadonly();
  }

  private _isConnectionInterruptedUiActive = signal(false);
  get isConnectionInterruptedUiActive() {
    return this._isConnectionInterruptedUiActive.asReadonly();
  }

  private currentApiBase: string | null = null;

  private _isSleeping = signal(false);
  get isSleeping() {
    return this._isSleeping.asReadonly();
  }

  private _healthStatus = signal<HealthCheckResult | null>(null);

  private _wakeupError = signal<string | null>(null);
  get wakeupError() {
    return this._wakeupError.asReadonly();
  }

  private _wakeupHint = signal<string | null>(null);
  get wakeupHint() {
    return this._wakeupHint.asReadonly();
  }

  private usingTipsFallback = signal(false);
  private selectedTipsLanguage = signal(this.getLanguageCode(this.translation.currentLang ?? this.translation.getBrowserLang()));
  private _loadingUserTips = signal(true);
  get loadingUserTips() {
    return this._loadingUserTips.asReadonly();
  }
  private selectedTipsLanguageObservable$ = toObservable(this.selectedTipsLanguage);
  private userTipsFromFresh$ = this.selectedTipsLanguageObservable$.pipe(
    switchMap(lang => this.helpdeskApiService.getTipsAndTricksArticles(lang).pipe(
      retry({
        count: 3,
        delay: (error: any, retryCount: number) => {
          if (error?.status === 401) {
            return throwError(() => error);
          }
          return timer(Math.min(5000, retryCount * 1000));
        },
      }),
      catchError(() => of({items: [] as TipsArticle[]})),
    )),
    map(response => response.items ?? []),
    tap((tips) => {
      if (!tips?.length) {
        if (!this.usingTipsFallback()) {
          this.usingTipsFallback.set(true);
          this.selectedTipsLanguage.set('no');
          return;
        }
        this._loadingUserTips.set(false);
        return;
      }
      this._loadingUserTips.set(false);
    })
  );
  private userTipsSignal = toSignal(this.userTipsFromFresh$);

  get userTips() {
    return this.userTipsSignal;
  }

  private _lastBackendReason = signal<string | null>(null);
  get lastBackendReason() {
    return this._lastBackendReason.asReadonly();
  }
  private lastWakeupErrorAt: number = 0;

  private inflightHealthCheck: { base: string, promise: Promise<boolean> } | null = null;
  private lastHealthCheckTime: number = 0;
  private currentHealthCheckStartedAt: number | null = null;
  private wakeUiDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private wakeRetryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Show wake UI only if wake flow lasts beyond a short debounce.
    effect(() => {
      if (this.wakeUiDebounceTimer) {
        clearTimeout(this.wakeUiDebounceTimer);
      }
      const wakeFlowActive = this._isWakeFlowActive();
      if (wakeFlowActive) {
        if (this._isWakeFlowUiActive()) {
          return;
        }
        this.wakeUiDebounceTimer = setTimeout(() => {
          this.wakeUiDebounceTimer = null;
          if (this._isWakeFlowActive()) {
            if (this._connectionInterrupted()) {
              // Some other thing?
            }
            else {
              this._isWakeFlowUiActive.set(true);
            }
          }
        }, TIMEOUT_WAKE_UI_DEBOUNCE_MS);
        return;
      }
      if (this.wakeUiDebounceTimer) {
        clearTimeout(this.wakeUiDebounceTimer);
        this.wakeUiDebounceTimer = null;
      }
      this._isWakeFlowUiActive.set(false);
    });
    this.translation.onLangChange.subscribe(event => {
      this.usingTipsFallback.set(false);
      this._loadingUserTips.set(true);
      this.selectedTipsLanguage.set(this.getLanguageCode(event.lang));
    });
  }

  enableHealthCheck() {
    this._skipHealthCheck.set(false);
  }

  disableHealthCheck() {
    this._skipHealthCheck.set(true);
    this.clearWakeRetryTimer();
  }

  get skipHealthCheck() {
    return this._skipHealthCheck();
  }

  clearWakeupError() {
    this._wakeupError.set(null);
    this._wakeupHint.set(null);
    this._lastBackendReason.set(null);
    this.lastWakeupErrorAt = 0;
    this.clearWakeRetryTimer();
    if (this._wakeState() === 'failed') {
      this._wakeState.set('idle');
    }
  }

  hasRecentWakeupError(blockMs: number = TIMEOUT_WAKEUP_ERROR_RETRY_BLOCK_MS): boolean {
    if (!this._wakeupError() || this._wakeState() !== 'failed') {
      return false;
    }
    return Date.now() - this.lastWakeupErrorAt < blockMs;
  }

  private getWakeupErrorCooldownRemainingMs(blockMs: number = TIMEOUT_WAKEUP_ERROR_RETRY_BLOCK_MS): number {
    if (!this._wakeupError() || this._wakeState() !== 'failed') {
      return 0;
    }
    return Math.max(0, blockMs - (Date.now() - this.lastWakeupErrorAt));
  }

  // Returns true only if this call woke a sleeping backend.
  async ensureAwake(reason: string = 'unknown'): Promise<boolean> {
    debugLog(`[hibernation]: ensureAwake called (${reason})`);
    return this.runWakeupCheck();
  }

  /**
   * Performs a health check against the selected API, triggering wakeup if it is hibernating.
   * Promise resolves when awake+healthy.
   * Long wait messaging is UI-only and does not stop recovery.
   * Unexpected failures are surfaced and retried after cooldown.
   */
  private async runWakeupCheck(): Promise<boolean> {
    let server: PrimusInstanceDetails;
    try {
      server = PrimusBackendInstanceService.getInstanceDetails();
      if (!server) {
        throw new Error('No server instance found');
      }
    } catch (e) {
      debugLog('[hibernation] No server instance found, skipping health check');
      return false;
    }

    const base = (server.api ?? '').replace(/\/+$/, '');
    this.syncServerContext(base);

    if (this.skipHealthCheck) {
      this._isSleeping.set(false);
      this._wakeState.set('idle');
      this.clearWakeRetryTimer();
      debugLog('[hibernation]: health check skipped');
      return false;
    }

    // Back off only after a real failed wake attempt.
    // Long-wait messaging should not introduce cooldown delays.
    if (this._wakeState() === 'failed' && this._wakeupError() && this.currentApiBase === base) {
      const remainingCooldownMs = this.getWakeupErrorCooldownRemainingMs();
      if (remainingCooldownMs > 0) {
        this._isSleeping.set(true);
        this._wakeState.set('checking-status');
        debugLog(`[hibernation]: previous wakeup failed; retrying after cooldown (${remainingCooldownMs} ms)`);
        await this.sleep(remainingCooldownMs);
      }
      debugLog('[hibernation]: retrying wakeup after failure cooldown');
    }

    if (this.inflightHealthCheck?.base === base) {
      debugLog('[hibernation]: health check already in progress, returning existing promise');
      return this.inflightHealthCheck.promise;
    }

    // Avoid spamming checks in normal operation, but never "cache skip" while sleeping.
    //
    // Important: backend can scale down while the SPA is still open. If /status reports "down",
    // we must not skip, even if we checked recently.
    if (!this._isSleeping() && Date.now() - this.lastHealthCheckTime < TIMEOUT_SKIP_RECENT_HEALTHCHECK_MS) {
      try {
        const status = await this.readBackendStatus(server);
        if (status.state === 'up' || status.state === 'unsupported') {
          debugLog('[hibernation]: health check already done recently, skipping');
          return false;
        }
        if (status.state === 'down') {
          debugLog('[hibernation]: /status indicates backend is down, forcing health check');
        } else {
          // Treat inconclusive status as "do not force" when we just checked.
          // This avoids rapid wake UI re-entry from short /status timeouts.
          debugLog('[hibernation]: /status is inconclusive, skipping forced health check');
          return false;
        }
      } catch {
        // If we cannot confirm "up", fall through and perform the real check.
      }
    }

    debugLog('[hibernation]: starting health check');
    // On retry after timeout/error, keep wake UI visible immediately.
    // This prevents a brief hide/show gap while wake error is cleared and debounce restarts.
    if (this._wakeupError()) {
      this._isWakeFlowUiActive.set(true);
    }
    this._wakeupError.set(null);
    this._wakeState.set('checking-status');
    this.currentHealthCheckStartedAt = Date.now();
    const promise = this.healthCheckInternal(server)
      .then((result) => {
        this.lastHealthCheckTime = Date.now();
        debugLog('[hibernation]: health check done');
        return result;
      })
      .catch((error) => {
        console.error('[hibernation]: health check failed:', error);
        this._isSleeping.set(false);
        this._wakeupError.set(this.getWakeupErrorMessage(server));
        this.lastWakeupErrorAt = Date.now();
        // Keep the latest wake hint visible during timeout/retry loops.
        // /status may still report "down" with a useful reason while wake error is shown.
        this._wakeState.set('failed');
        // Keep wake recovery alive even if no new user-triggered requests arrive.
        // This prevents getting stuck on splash until a hard refresh.
        this.scheduleWakeRetryAfterError();
        return false;
      })
      .finally(() => {
        debugLog('[hibernation]: health check done, clearing inflight promise');
        this.inflightHealthCheck = null;
        this.currentHealthCheckStartedAt = null;
      });

    this.inflightHealthCheck = { base, promise };
    return promise;
  }

  private syncServerContext(base: string) {
    if (!base) {
      return;
    }
    if (this.currentApiBase !== base) {
      this.currentApiBase = base;
      this.lastHealthCheckTime = 0;
      this._healthStatus.set(null);
      this._isSleeping.set(false);
      this._wakeupError.set(null);
      this._wakeupHint.set(null);
      this._lastBackendReason.set(null);
      this.lastWakeupErrorAt = 0;
      this._wakeState.set('idle');
      this.clearWakeRetryTimer();
    }
  }

  private buildStatusUrl(server: PrimusInstanceDetails) {
    const base = (server.api ?? '').replace(/\/+$/, '');
    // Keep this without a trailing slash to match ingress routing reliably.
    return this.withServiceWorkerBypass(`${base}/status`);
  }

  private buildHealthUrl(server: PrimusInstanceDetails) {
    const base = (server.api ?? '').replace(/\/+$/, '');
    return this.withServiceWorkerBypass(`${base}/healthcheck/check_health/all`);
  }

  // Avoid Angular service-worker mediation for wake probes.
  // This keeps status/health checks as direct network calls during hibernation recovery.
  private withServiceWorkerBypass(url: string): string {
    try {
      const parsed = new URL(url);
      parsed.searchParams.set('ngsw-bypass', 'true');
      return parsed.toString();
    } catch {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}ngsw-bypass=true`;
    }
  }

  private async readBackendStatus(server: PrimusInstanceDetails): Promise<BackendStatusSnapshot> {
    const url = this.buildStatusUrl(server);
    try {
      const response = await fetchWithTimeout(url, {}, TIMEOUT_STATUS_FETCH_MS);
      if (response.status === 404) {
        // Treat as "cannot use /status"; healthCheck will fall back to /health polling.
        this._wakeupHint.set(null);
        this._lastBackendReason.set(null);
        this._connectionInterrupted.set(false);
        return {
          state: 'unsupported',
          reason: null,
          retryAfterMs: TIMEOUT_RETRY_HEALTHCHECK_AFTER_MS,
        };
      }
      if (!response.ok) {
        this._connectionInterrupted.set(true);
        return {
          state: 'unknown',
          reason: null,
          retryAfterMs: TIMEOUT_RETRY_HEALTHCHECK_AFTER_MS,
        };
      }
      const statusData: BackendStatusResult | null = await response.json().catch(() => null);
      const status = statusData?.backendstatus;
      const reason = this.normalizeStatusReason(statusData?.reason);
      const retryAfterMs = this.parseRetryAfterMs(statusData?.retryAfterSeconds);
      if (status === 'up') {
        this._connectionInterrupted.set(false);
        this._wakeupHint.set(null);
        this._lastBackendReason.set(null);
        return {state: 'up', reason: null, retryAfterMs};
      }
      if (status === 'down') {
        this._connectionInterrupted.set(false);
        this._lastBackendReason.set(reason);
        this._wakeupHint.set(this.getWakeupHintMessage(server, reason));
        return {state: 'down', reason, retryAfterMs};
      }

      this._connectionInterrupted.set(true);
      return {
        state: 'unknown',
        reason: null,
        retryAfterMs: TIMEOUT_RETRY_HEALTHCHECK_AFTER_MS,
      };
    } catch (e) {
      this._connectionInterrupted.set(true);
      return {
        state: 'unknown',
        reason: null,
        retryAfterMs: TIMEOUT_RETRY_HEALTHCHECK_AFTER_MS,
      };
    }
  }

  private async waitForBackendToReportUp(server: PrimusInstanceDetails): Promise<boolean> {
    while (true) {
      if (this.skipHealthCheck) {
        this._isSleeping.set(false);
        return false;
      }
      this._wakeState.set('waiting-for-up');
      this.updateLongWakeMessageIfNeeded(server);
      const status = await this.readBackendStatus(server);
      if (status.state === 'up' || status.state === 'unsupported') {
        return true;
      }

      // /status is now treated as passive state reading only.
      // Wakeup is triggered by calling the backend health endpoint on the regular API path.
      const wakeTriggered = await this.triggerWakeup(server);
      // If /status is temporarily unreachable but the wake trigger returns 200,
      // continue to deep health polling instead of getting stuck in status phase.
      if (status.state === 'unknown' && wakeTriggered) {
        return true;
      }

      this._isSleeping.set(true);
      const waitMs = wakeTriggered
        ? Math.min(status.retryAfterMs, STATUS_RETRY_AFTER_MIN_MS)
        : status.retryAfterMs;
      await this.sleep(waitMs);
    }
  }

  private async triggerWakeup(server: PrimusInstanceDetails): Promise<boolean> {
    try {
      const response = await fetchWithTimeout(
        this.buildHealthUrl(server),
        {},
        TIMEOUT_WHEN_SYSTEM_IS_CONSIDERED_HIBERNATING_MS
      );
      return response.ok;
    } catch {
      // Intentionally ignored: wake trigger is best-effort.
      // Status polling loop remains the source of truth for readiness.
      return false;
    }
  }

  private async healthCheckInternal(server: PrimusInstanceDetails): Promise<boolean> {
    if (this.skipHealthCheck) {
      return false;
    }

    const initialStatus = await this.readBackendStatus(server);
    if (initialStatus.state === 'unsupported') {
      // Endpoint missing: treat as "no hibernation feature" and do not block.
      this.setAwakeAndClearWakeSignals();
      return false;
    }

    if (initialStatus.state === 'up') {
      this.setAwakeAndClearWakeSignals();
      return false;
    }

    // Anything but "up" is treated as hibernating/unavailable: block until it comes back.
    this._isSleeping.set(true);
    while (true) {
      const backendReportedUp = await this.waitForBackendToReportUp(server);
      if (!backendReportedUp) {
        return false;
      }

      // Backend reports up, but may still be initializing (db/solr). Wait for healthcheck to succeed.
      this._wakeState.set('checking-health');
      this._wakeupHint.set(null);
      const healthResponse = await this.fetchHealthStatus(server);
      if (!healthResponse) {
        // Backend can flap between up/down while dependencies are warming up.
        // If it drops back to down during health polling, restart from status phase.
        continue;
      }
      this.setAwakeAndClearWakeSignals();
      return true;
    }
  }

  private async fetchHealthStatus(server: PrimusInstanceDetails): Promise<HealthCheckResult | null> {
    while (true) {
      if (this.skipHealthCheck) {
        this._isSleeping.set(false);
        return null;
      }
      this.updateLongWakeMessageIfNeeded(server);
      try {
        const ping = await fetchWithTimeout(this.buildHealthUrl(server), {}, TIMEOUT_HEALTHCHECK_FETCH_MS);
        if (!ping.ok) {
          if (await this.backendDroppedToDown(server)) {
            return null;
          }
          if (ping.status === 503) {
            console.warn(`[hibernation] server ${server.api} is unavailable, retrying in ${TIMEOUT_RETRY_HEALTHCHECK_AFTER_MS} ms `);
            await this.sleep(TIMEOUT_RETRY_HEALTHCHECK_AFTER_MS);
            continue;
          }
          throw new Error(`Failed to fetch health status, status code: ${ping.status}`);
        }
        const healthResponse: HealthCheckResult = await ping.json();
        this._healthStatus.set(healthResponse);
        return healthResponse;
      } catch (error) {
        if (await this.backendDroppedToDown(server)) {
          return null;
        }
        console.warn('[hibernation] Error during healthcheck:', error);
        console.info(`[hibernation] trying again in ${TIMEOUT_RETRY_HEALTHCHECK_AFTER_MS} ms`);
        await this.sleep(TIMEOUT_RETRY_HEALTHCHECK_AFTER_MS);
      }
    }
  }

  private async backendDroppedToDown(server: PrimusInstanceDetails): Promise<boolean> {
    try {
      const status = await this.readBackendStatus(server);
      return status.state === 'down';
    } catch {
      return false;
    }
  }

  private sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private setAwakeAndClearWakeSignals() {
    this._isSleeping.set(false);
    this._wakeupError.set(null);
    this._wakeupHint.set(null);
    this._wakeState.set('idle');
    this.clearWakeRetryTimer();
  }

  private clearWakeRetryTimer() {
    if (!this.wakeRetryTimer) {
      return;
    }
    clearTimeout(this.wakeRetryTimer);
    this.wakeRetryTimer = null;
  }

  private scheduleWakeRetryAfterError() {
    this.clearWakeRetryTimer();
    const delayMs = this.getWakeupErrorCooldownRemainingMs() || TIMEOUT_WAKEUP_ERROR_RETRY_BLOCK_MS;
    this.wakeRetryTimer = setTimeout(() => {
      this.wakeRetryTimer = null;
      if (this.skipHealthCheck || !this._wakeupError()) {
        return;
      }
      this.ensureAwake('auto-retry:wakeup-error')
        .catch(() => undefined)
        .finally(() => {
          if (!this.skipHealthCheck && this._wakeupError()) {
            this.scheduleWakeRetryAfterError();
          }
        });
    }, delayMs);
  }

  private updateLongWakeMessageIfNeeded(server: PrimusInstanceDetails) {
    if (!this.currentHealthCheckStartedAt) {
      return;
    }
    const elapsed = Date.now() - this.currentHealthCheckStartedAt;
    if (elapsed < TIMEOUT_WAKE_LONG_WAIT_MESSAGE_MS) {
      return;
    }
    if (this._wakeupError()) {
      return;
    }
    this._wakeupError.set(this.getLongWakeMessage(server));
    this.lastWakeupErrorAt = Date.now();
  }

  private normalizeStatusReason(reason: unknown): string | null {
    if (typeof reason !== 'string') {
      return null;
    }
    const trimmed = reason.trim();
    return trimmed ? trimmed : null;
  }

  private parseRetryAfterMs(retryAfterSeconds: unknown): number {
    const parsed = Number(retryAfterSeconds);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return TIMEOUT_RETRY_HEALTHCHECK_AFTER_MS;
    }
    const retryAfterMs = Math.round(parsed * 1000);
    return Math.min(STATUS_RETRY_AFTER_MAX_MS, Math.max(STATUS_RETRY_AFTER_MIN_MS, retryAfterMs));
  }

  private getLanguageCode(preferredLang?: string) {
    if (['no', 'sv', 'en'].includes(preferredLang || '')) {
      return preferredLang || 'no';
    }
    return 'no';
  }

  private getWakeupHintMessage(server: PrimusInstanceDetails, reason: string | null): string {
    const serverName = server.name ?? 'Museet';
    switch (reason) {
      case 'DB_STARTING':
        return this.translation.instant(
          'TRANS__HIBERNATION__WAKE_HINT__DB_STARTING',
          {serverName}
        );
      case 'DB_READ_ONLY':
        return this.translation.instant(
          'TRANS__HIBERNATION__WAKE_HINT__DB_READ_ONLY',
          {serverName}
        );
      case 'DB_UNREACHABLE':
        return this.translation.instant(
          'TRANS__HIBERNATION__WAKE_HINT__DB_UNREACHABLE',
          {serverName}
        );
      case 'SOLR_STARTING':
      case 'SOLR_UNAVAILABLE':
        return this.translation.instant(
          'TRANS__HIBERNATION__WAKE_HINT__SOLR_STARTING',
          {serverName}
        );
      case 'BACKEND_UNREACHABLE':
      case 'BACKEND_UNAVAILABLE':
      case 'PROXY_TIMEOUT':
        return this.translation.instant(
          'TRANS__HIBERNATION__WAKE_HINT__SERVICES_STARTING',
          {serverName}
        );
      default:
        return this.translation.instant(
          'TRANS__HIBERNATION__WAKE_HINT__GENERIC',
          {serverName}
        );
    }
  }

  private getWakeupFailureDetail(reason: string | null): string {
    switch (reason) {
      case 'DB_STARTING':
        return this.translation.instant('TRANS__HIBERNATION__WAKE_FAILURE_DETAIL__DB_STARTING');
      case 'DB_READ_ONLY':
        return this.translation.instant('TRANS__HIBERNATION__WAKE_FAILURE_DETAIL__DB_READ_ONLY');
      case 'DB_UNREACHABLE':
        return this.translation.instant('TRANS__HIBERNATION__WAKE_FAILURE_DETAIL__DB_UNREACHABLE');
      case 'SOLR_STARTING':
        return this.translation.instant('TRANS__HIBERNATION__WAKE_FAILURE_DETAIL__SOLR_STARTING');
      case 'SOLR_UNAVAILABLE':
        return this.translation.instant('TRANS__HIBERNATION__WAKE_FAILURE_DETAIL__SOLR_UNAVAILABLE');
      case 'BACKEND_UNREACHABLE':
        return this.translation.instant('TRANS__HIBERNATION__WAKE_FAILURE_DETAIL__BACKEND_UNREACHABLE');
      case 'BACKEND_UNAVAILABLE':
      case 'PROXY_TIMEOUT':
        return this.translation.instant('TRANS__HIBERNATION__WAKE_FAILURE_DETAIL__BACKEND_UNAVAILABLE');
      default:
        return this.translation.instant('TRANS__HIBERNATION__WAKE_FAILURE_DETAIL__GENERIC');
    }
  }

  private getLongWakeMessage(server: PrimusInstanceDetails): string {
    const serverName = server.name ?? 'Systemet';
    const reason = this._lastBackendReason();
    const detail = this.getWakeupFailureDetail(reason);
    return this.translation.instant(
      'TRANS__HIBERNATION__WAKE_ERROR__TIMEOUT',
      {
        serverName,
        timeoutSec: Math.floor(TIMEOUT_WAKE_LONG_WAIT_MESSAGE_MS / 1000),
        detail,
      }
    );
  }

  private getWakeupErrorMessage(server: PrimusInstanceDetails): string {
    const serverName = server.name ?? 'Systemet';
    return this.translation.instant(
      'TRANS__HIBERNATION__WAKE_ERROR__GENERIC',
      {serverName}
    );
  }
}

function debugLog(msg: string) {
  if (isDevMode()) {
    console.debug(msg);
  }
}
